import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";

import { AUTHENTICATION_CHANGED_EVENT } from "@/lib/authentication-events";
import AuthService from "@/services/auth";
import Settings from "@/services/settings";

import type { AuthoritativeAccountContext } from "@/services/settings";

export type CurrentAccountDetails = {
  _id?: string;
  name?: string;
  owner?: string;
  world?: string;
  groups?: string[];
  activityCoverDefaults?: unknown;
  email?: string;
  signupProfile?: "teacher" | "school";
  signupPlanKey?: string;
  "new-manager-global"?: boolean;
  [key: string]: unknown;
};

export function getCurrentAccountFromSettings(): CurrentAccountDetails | undefined {
  const settings = Settings.getSettings();
  return getCurrentAccountFromSnapshot(settings);
}

function getCurrentAccountFromSnapshot(settings: ReturnType<typeof Settings.getSettings>): CurrentAccountDetails | undefined {
  if (!settings?.accountsDetails?.length) return undefined;

  const bySessionWorld = settings.accountsDetails.find((account: CurrentAccountDetails) => account.world === settings.schoolName || account.name === settings.schoolName);
  return bySessionWorld || settings.accountsDetails[0];
}

export function useCurrentAccount() {
  // Decisões de coorte/redirect não podem usar o cache local antes da
  // reconciliação autoritativa: ele pode pertencer à conta anterior.
  const [account, setAccount] = useState<CurrentAccountDetails | undefined>();

  useEffect(() => {
    let active = true;
    let hydration = 0;

    const hydrateAuthenticatedAccount = () => {
      const currentHydration = ++hydration;
      if (!AuthService.isLogged()) {
        setAccount(undefined);
        return;
      }

      const cachedAccount = getCurrentAccountFromSettings();
      void Settings.loadSettings().then(() => {
        if (active && currentHydration === hydration) {
          setAccount(getCurrentAccountFromSettings() ?? cachedAccount);
        }
      }).catch(() => {
        // Em falha de rede, preserva o último cache utilizável sem liberar uma
        // rejection solta no React.
        if (active && currentHydration === hydration) setAccount(cachedAccount);
      });
    };

    hydrateAuthenticatedAccount();
    window.addEventListener(AUTHENTICATION_CHANGED_EVENT, hydrateAuthenticatedAccount);

    return () => {
      active = false;
      hydration += 1;
      window.removeEventListener(AUTHENTICATION_CHANGED_EVENT, hydrateAuthenticatedAccount);
    };
  }, []);

  return account;
}

function getCurrentAccountFromAuthoritativeContext(context: AuthoritativeAccountContext | undefined): CurrentAccountDetails | undefined {
  if (!context?.userInfo?.accountsDetails?.length) return undefined;

  const { settings, userInfo } = context;
  const sessionAccountId = userInfo.sessionAccountId;
  return (
    userInfo.accountsDetails.find(account => account._id === sessionAccountId) ??
    userInfo.accountsDetails.find(account => account.world === settings.worldId) ??
    userInfo.accountsDetails.find(account => account.world === settings.schoolName || account.name === settings.schoolName)
  );
}

export type FreshAccountResolution = { status: "pending"; account: undefined } | { status: "resolved"; account: CurrentAccountDetails } | { status: "error"; account: undefined };

type StoredFreshAccountResolution = FreshAccountResolution & { sessionIdentity: string };

function getCachedSessionIdentity(): string {
  const settings = Settings.getSettings();
  if (!settings) return "missing";

  return `present:${settings.worldId ?? ""}:${settings.schoolName ?? ""}`;
}

/**
 * Revalida a conta diretamente na API antes de liberar uma superfície
 * financeira. O snapshot local serve apenas para detectar troca/revogação da
 * sessão e nunca é retornado como autorização.
 */
export function useFreshCurrentAccountResolution() {
  const sessionIdentity = useSyncExternalStore(
    listener => Settings.subscribe(listener),
    getCachedSessionIdentity,
    () => "server"
  );
  const [retryId, setRetryId] = useState(0);
  const requestIdRef = useRef(0);
  const lastResolvedIdentityRef = useRef<string | null>(null);
  const [resolution, setResolution] = useState<StoredFreshAccountResolution>({
    status: "pending",
    account: undefined,
    sessionIdentity,
  });

  useEffect(() => {
    if (lastResolvedIdentityRef.current === sessionIdentity) return;

    const requestId = ++requestIdRef.current;
    setResolution({ status: "pending", account: undefined, sessionIdentity });

    void Settings.loadAuthoritativeAccountContext()
      .then(context => {
        if (requestId !== requestIdRef.current) return;
        const account = getCurrentAccountFromAuthoritativeContext(context);
        if (!account) {
          lastResolvedIdentityRef.current = null;
          setResolution({ status: "error", account: undefined, sessionIdentity });
          return;
        }

        lastResolvedIdentityRef.current = sessionIdentity;
        setResolution({ status: "resolved", account, sessionIdentity });
      })
      .catch(() => {
        if (requestId !== requestIdRef.current) return;
        lastResolvedIdentityRef.current = null;
        setResolution({ status: "error", account: undefined, sessionIdentity });
      });

    return () => {
      if (requestId === requestIdRef.current) requestIdRef.current += 1;
    };
  }, [sessionIdentity, retryId]);

  const retry = useCallback(() => {
    lastResolvedIdentityRef.current = null;
    setRetryId(value => value + 1);
  }, []);

  const currentResolution: FreshAccountResolution =
    resolution.sessionIdentity === sessionIdentity ? resolution : { status: "pending", account: undefined };

  return { ...currentResolution, retry };
}
